import React, { useState } from "react";
import { Card, Row, Col, Tabs, Tab } from "react-bootstrap";
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import Button from "../../Elements/Button";
import CustomTooltip from "../../Elements/Tooltip";
import ColumnChart from "./Charts/ColumnChart";
import ShowCharts from "./Charts/ShowCharts";
import SelectChart from "./Charts/SelectChart";
import { useStats } from "./StatsContext";

const AtAGlance = () => {
    const { selectedRecipientId, receiverStats, receiverStatsLoading, chartStats, chartStatsLoading } = useStats();

    const [selectedChart, setSelectedChart] = useState({
        browser: 'Pie',
        os: 'Pie',
        device: 'Pie',
        city: 'Pie',
        region: 'Pie',
    });

    const [pageVisitFilter, setPageVisitFilter] = useState('most');

    const { tl, doc } = window?.objInit;
    const defaultStats = (tl || doc)?.stats;

    // Priority: receiver stats > chart stats (threshold-filtered) > default
    const stats = (selectedRecipientId && receiverStats) ? receiverStats : (chartStats || defaultStats);

    const mostViewed = stats?.most_visited_pages?.reduce((max, obj) => {
        return obj.views > max.views ? obj : max;
    }, stats?.most_visited_pages[0]);

    const handleSelectChange = ({ target: { value } }, type) => {
        setSelectedChart((prevState) => ({
            ...prevState,
            [type]: value,
        }));
    };

    if (receiverStatsLoading || chartStatsLoading) {
        return (
            <div className="laserlink-stats-container">
                <Row>
                    <Col md={12}>
                        <h4>Overview</h4>
                    </Col>
                </Row>
                <Row>
                    <Col md={12} className="text-center py-5">
                        <div className="spinner-border text-secondary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2 text-muted">Loading recipient stats...</p>
                    </Col>
                </Row>
            </div>
        );
    }

    return (
        <div className="laserlink-stats-container">
            <Row>
                <Col md={12}>
                    <h4>Overview</h4>
                </Col>
            </Row>
            <Row>
                <Col md={6}>
                    <Card className="p-3 h-100 d-flex">
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    <h5>Performance</h5>
                                    <p><CustomTooltip title={<p className="p-1 text-light">Total sessions where time spent met the minimum view threshold</p>}><strong className="cursor-pointer">{stats?.viewers}</strong></CustomTooltip> <br /> Visits</p>
                                    <p><CustomTooltip title={<p className="p-1 text-light">HH:MM:SS — Combined time all visitors spent viewing</p>}><strong className="cursor-pointer">{stats?.total_min_viewed}</strong></CustomTooltip> <br /> Total Time</p>
                                    <p><CustomTooltip title={<p className="p-1 text-light">Average percentage of pages viewed per session</p>}><strong className="cursor-pointer">{stats?.percent_viewed}</strong></CustomTooltip> <br /> Viewed</p>
                                    <p><CustomTooltip title={<p className="p-1 text-light">Page with the highest number of views across all sessions</p>}><strong className="cursor-pointer">{mostViewed?.page}</strong></CustomTooltip> <br /> Most Visited Page</p>
                                </Col>
                                <Col md={6}>
                                    <h5>Behavioral metrics</h5>
                                    <p><CustomTooltip title={<p className="p-1 text-light">Visitors with more than one visit</p>}><strong className="cursor-pointer">{stats?.returning_viewers}</strong></CustomTooltip> <br /> Returning Viewers</p>
                                    <p><CustomTooltip title={<p className="p-1 text-light">Total unique recipients who opted in</p>}><strong className="cursor-pointer">{stats?.optins_num}</strong></CustomTooltip> <br /> Opt-ins</p>
                                    <p><CustomTooltip title={<p className="p-1 text-light">Visitors who watched a video element to 100%</p>}><strong className="cursor-pointer">0/0</strong></CustomTooltip> <br /> Viewed Video 100%</p>
                                    <p><CustomTooltip title={<p className="p-1 text-light">Engagement score based on sessions and pages viewed on engagement pages</p>}><strong className="cursor-pointer">{stats?.engagement?.replace("%", "")}</strong></CustomTooltip> <br /> Score</p>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={6}>
                    <Tabs
                        defaultActiveKey={"pageVisits"}
                        id="uncontrolled-tab-example"
                        className="select-file-pop stats-nav"
                    >
                        <Tab
                            eventKey="pageVisits"
                            title={'Page Visits'}
                        >
                            <div>
                                <div className="d-flex justify-content-end" style={{ padding: "10px 10px 0" }}>
                                    <select
                                        value={pageVisitFilter}
                                        onChange={(e) => setPageVisitFilter(e.target.value)}
                                        style={{
                                            padding: "4px 8px",
                                            borderRadius: "6px",
                                            fontSize: "12px",
                                            border: "1px solid #ccc",
                                            cursor: "pointer",
                                            outline: "none",
                                        }}
                                    >
                                        <option value="most">Most Visited</option>
                                        <option value="least">Least Visited</option>
                                        <option value="none">No Visits</option>
                                    </select>
                                </div>
                                {pageVisitFilter === 'most' && (
                                    <ColumnChart data={stats?.most_visited_pages} name={'page_views'} xAxisLabel="Pages" yAxisLabel="Total visits" sortDescending />
                                )}
                                {pageVisitFilter === 'least' && (
                                    <ColumnChart data={stats?.least_visited_pages} name={'page_views'} xAxisLabel="Pages" yAxisLabel="Total visits" sortAscending />
                                )}
                                {pageVisitFilter === 'none' && (
                                    stats?.unviewed_pages?.length > 0 ? (
                                        <HighchartsReact
                                            highcharts={Highcharts}
                                            options={{
                                                chart: { type: 'column' },
                                                title: { text: '' },
                                                xAxis: {
                                                    categories: stats.unviewed_pages.map((item) => item.page),
                                                    title: { text: 'Pages' },
                                                },
                                                yAxis: {
                                                    title: { text: 'Total visits' },
                                                    max: 1,
                                                    tickInterval: 1,
                                                },
                                                legend: { enabled: false },
                                                tooltip: {
                                                    formatter: function () {
                                                        return `<b>Page ${this.point.category}</b><br/>0 Visits`;
                                                    },
                                                },
                                                series: [{
                                                    name: 'page_views',
                                                    data: stats.unviewed_pages.map(() => ({
                                                        y: 0,
                                                        color: '#e74c3c',
                                                    })),
                                                }],
                                                plotOptions: {
                                                    column: {
                                                        borderColor: '#e74c3c',
                                                        borderWidth: 2,
                                                        pointWidth: 20,
                                                        minPointLength: 4,
                                                    },
                                                },
                                            }}
                                        />
                                    ) : (
                                        <p className="text-center text-muted py-5">All pages have been visited</p>
                                    )
                                )}
                            </div>
                        </Tab>
                        <Tab
                            eventKey="timePerPage"
                            title={'Time per Page'}
                        >
                            <div className="">
                                <ColumnChart data={stats?.time_per_page} name={'total_secs_on_page'} xAxisLabel="Pages" yAxisLabel="Total duration (sec)" formatTooltipAsDuration sortDescending />
                            </div>
                        </Tab>
                    </Tabs>
                </Col>
            </Row>
            <Row>
                <Col md={6}>
                    <Tabs defaultActiveKey={"browser"} id="uncontrolled-tab-example" className="mt-4 select-file-pop stats-nav">
                        <Tab eventKey="browser" title={'Browser'}>
                            <div className="">
                                <SelectChart selectedChart={selectedChart} handleSelectChange={handleSelectChange} name='browser' />
                                <ShowCharts selectedChart={selectedChart['browser']} data={stats?.by_browser} name='browser' />
                            </div>
                        </Tab>
                        <Tab eventKey="os" title={'OS'}>
                            <div className="">
                                <SelectChart selectedChart={selectedChart} handleSelectChange={handleSelectChange} name='os' />
                                <ShowCharts selectedChart={selectedChart['os']} data={stats?.by_os} name='os_family' />
                            </div>
                        </Tab>
                        <Tab eventKey="device" title={'Device'}>
                            <div className="">
                                <SelectChart selectedChart={selectedChart} handleSelectChange={handleSelectChange} name='device' />
                                <ShowCharts selectedChart={selectedChart['device']} name='device' data={stats?.by_device} />
                            </div>
                        </Tab>
                    </Tabs>
                </Col>
                <Col>
                    <Tabs defaultActiveKey={"city"} id="uncontrolled-tab-example" className="mt-4 select-file-pop cursor-pointer stats-nav">
                        <Tab eventKey="city" title={'City'}>
                            <div className="">
                                <SelectChart selectedChart={selectedChart} handleSelectChange={handleSelectChange} name='city' />
                                <ShowCharts selectedChart={selectedChart['city']} data={stats?.by_city} name='city' />
                            </div>
                        </Tab>
                        <Tab eventKey="region" title={'Region'}>
                            <div className="">
                                <SelectChart selectedChart={selectedChart} handleSelectChange={handleSelectChange} name='region' />
                                <ShowCharts selectedChart={selectedChart['region']} data={stats?.by_subdivision} name='subdivision' />
                            </div>
                        </Tab>
                    </Tabs>
                </Col>
            </Row>
        </div>
    );
}
export default AtAGlance;
